/**
 * @module public/js/focusmode/tooltipService.js
 * @summary Accessible tooltip helper for Focus Mode controls.
 *
 * Adds lightweight ARIA hints and custom bubble tooltips to key Focus Mode
 * affordances when the user has enabled tooltips. Cleans up any previously
 * injected markup so repeated renders stay idempotent.
 */

const TOOLTIP_ATTR = 'data-focus-tooltip-id';
const TOOLTIP_TEXT_ATTR = 'data-focus-tooltip';
const TOOLTIP_SIGNATURE_ATTR = 'data-focus-tooltip-signature';
const TOOLTIP_ROOT_ID = 'tooltip-root';
const TOOLTIP_OFFSET_PX = 8;
const FILTER_TOOLTIP_CONTAINERS =
  '#taskFilters, #taskTypeFilters, #playbookFilters';
const FILTER_TOOLTIP_BUTTONS = FILTER_TOOLTIP_CONTAINERS.split(',')
  .map((selector) => `${selector.trim()} button`)
  .join(', ');
const TOOLTIP_CONFIG = [
  {
    selector: '#ctaSlot button, #ctaSlot a',
    text: 'Complete this step to move to the next task.',
    placement: 'top',
  },
  {
    selector: '#replySlot button, #replySlot a',
    text: 'Send quick replies without leaving Focus Mode.',
    placement: 'top',
  },
  {
    selector: '.lead-card__quick-links [data-cta="linkedin"]',
    text: 'Open the LinkedIn profile in a new tab.',
  },
  {
    selector: '.lead-card__quick-links .hubspot-link',
    text: 'Open the HubSpot record in a new tab.',
  },
  {
    selector: '.lead-card__quick-links #editContactBtn',
    text: 'Edit this contact’s details.',
  },
  {
    selector: '.ai-accordion > summary',
    text: 'Expand to view AI research and saved notes.',
    placement: 'bottom',
  },
  {
    selector: FILTER_TOOLTIP_BUTTONS,
    text: 'Filter today’s work by activity or playbook.',
    placement: 'bottom',
  },
];

const BLOCK_TOOLTIP_DISPLAYS = new Set([
  'block',
  'flex',
  'grid',
  'list-item',
  'table',
  'table-row',
  'table-cell',
]);

const OFFSCREEN_POSITION = '-9999px';
const ACTIVE_TOOLTIP_TARGETS = new Map();
const TOOLTIP_ORIGINS = new Map();
const TOOLTIP_LISTENERS = new Map();
const FILTER_TOOLTIP_DELEGATION_DOCS = new WeakSet();
const GLOBAL_TOOLTIP_LISTENER_DOCS = new WeakSet();
const FILTER_TIP_BY_BUTTON = new WeakMap();
let TOOLTIP_ID_SEQ = 0;
let resizeDebounceId = null;
let resizeListenerAttached = false;
let scrollListenerAttached = false;
let lastInputWasKeyboard = false;

/**
 * Debounced resize handler to reposition active tooltip bubbles.
 *
 * @returns {void}
 */
function handleTooltipResize() {
  if (typeof window === 'undefined') return;
  if (!ACTIVE_TOOLTIP_TARGETS.size) return;
  if (resizeDebounceId) {
    window.clearTimeout(resizeDebounceId);
  }
  resizeDebounceId = window.setTimeout(() => {
    ACTIVE_TOOLTIP_TARGETS.forEach(({ tip, update }, target) => {
      if (!target.isConnected || !tip.isConnected) {
        hideTooltip(target, tip);
        return;
      }
      update();
    });
  }, 120);
}

/**
 * Ensure the tooltip portal root exists at the end of the document body.
 *
 * @param {Document} doc - Document to attach the tooltip root.
 * @returns {HTMLElement|null} Tooltip root element.
 */
function ensureTooltipRoot(doc) {
  if (!doc?.body) return null;
  let root = doc.getElementById(TOOLTIP_ROOT_ID);
  if (root) return root;
  root = doc.createElement('div');
  root.id = TOOLTIP_ROOT_ID;
  doc.body.appendChild(root);
  return root;
}

/**
 * Move a tooltip bubble into the portal root, recording its origin.
 *
 * @param {HTMLElement} tip - Tooltip element to move.
 * @returns {void}
 */
function mountTooltipInPortal(tip) {
  if (!tip) return;
  const doc = tip.ownerDocument || document;
  const root = ensureTooltipRoot(doc);
  if (!root) return;
  if (!TOOLTIP_ORIGINS.has(tip)) {
    TOOLTIP_ORIGINS.set(tip, {
      parent: tip.parentElement,
      nextSibling: tip.nextSibling,
    });
  }
  if (tip.parentElement !== root) {
    root.appendChild(tip);
  }
}

/**
 * Restore a tooltip bubble back to its original DOM location.
 *
 * @param {HTMLElement} tip - Tooltip element to restore.
 * @returns {void}
 */
function restoreTooltipFromPortal(tip) {
  if (!tip) return;
  const origin = TOOLTIP_ORIGINS.get(tip);
  if (!origin) return;
  const { parent, nextSibling } = origin;
  if (parent?.isConnected) {
    parent.insertBefore(tip, nextSibling || null);
  } else {
    tip.remove();
  }
  TOOLTIP_ORIGINS.delete(tip);
}

/**
 * Hide a tooltip bubble and clear active state tracking.
 *
 * @param {HTMLElement} target - Tooltip target element.
 * @param {HTMLElement} tip - Tooltip bubble element.
 * @returns {void}
 */
export function hideTooltip(target, tip) {
  if (!tip) return;
  tip.classList.remove('focus-tooltip--active');
  tip.style.left = OFFSCREEN_POSITION;
  tip.style.top = OFFSCREEN_POSITION;
  restoreTooltipFromPortal(tip);
  if (target) {
    ACTIVE_TOOLTIP_TARGETS.delete(target);
  }
}

/**
 * Activate a tooltip bubble after positioning it.
 *
 * @param {HTMLElement} target - Tooltip target element.
 * @param {HTMLElement} tip - Tooltip bubble element.
 * @returns {void}
 */
export function activateTooltip(target, tip) {
  if (!target || !tip) return;
  if (!target.isConnected || !tip.isConnected) {
    hideTooltip(target, tip);
    return;
  }

  const updatePosition = () => {
    if (!target.isConnected || !tip.isConnected) {
      hideTooltip(target, tip);
      return;
    }
    positionTooltipBubble(target, tip);
  };

  ACTIVE_TOOLTIP_TARGETS.forEach(({ tip: activeTip }, activeTarget) => {
    if (activeTarget !== target) {
      hideTooltip(activeTarget, activeTip);
    }
  });

  mountTooltipInPortal(tip);
  tip.classList.remove('focus-tooltip--active');
  updatePosition();
  tip.classList.add('focus-tooltip--active');
  ACTIVE_TOOLTIP_TARGETS.set(target, {
    tip,
    update: updatePosition,
  });

  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(updatePosition);
  } else {
    setTimeout(updatePosition, 0);
  }
}

/**
 * Deactivate a tooltip bubble and clear its position.
 *
 * @param {HTMLElement} target - Tooltip target element.
 * @param {HTMLElement} tip - Tooltip bubble element.
 * @returns {void}
 */
export function deactivateTooltip(target, tip) {
  hideTooltip(target, tip);
}

/**
 * Attach shared document listeners for focus modality and global dismissal.
 *
 * @param {Document} doc - Document hosting tooltips.
 * @returns {void}
 */
function attachGlobalTooltipListeners(doc) {
  if (!doc || GLOBAL_TOOLTIP_LISTENER_DOCS.has(doc)) return;
  GLOBAL_TOOLTIP_LISTENER_DOCS.add(doc);
  doc.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Escape') {
        ACTIVE_TOOLTIP_TARGETS.forEach(({ tip }, target) =>
          hideTooltip(target, tip)
        );
        return;
      }
      lastInputWasKeyboard = true;
    },
    { capture: true }
  );
  doc.addEventListener(
    'pointerdown',
    (event) => {
      lastInputWasKeyboard = false;
      ACTIVE_TOOLTIP_TARGETS.forEach(({ tip }, target) => {
        if (!target.contains(event.target)) {
          hideTooltip(target, tip);
        }
      });
    },
    { capture: true }
  );
  doc.addEventListener(
    'pointercancel',
    () => {
      ACTIVE_TOOLTIP_TARGETS.forEach(({ tip }, target) =>
        hideTooltip(target, tip)
      );
    },
    { capture: true }
  );
  doc.addEventListener('visibilitychange', () => {
    if (!doc.hidden) return;
    ACTIVE_TOOLTIP_TARGETS.forEach(({ tip }, target) =>
      hideTooltip(target, tip)
    );
  });

  const win = doc.defaultView;
  ['blur', 'pagehide'].forEach((eventName) => {
    win?.addEventListener(eventName, () => {
      ACTIVE_TOOLTIP_TARGETS.forEach(({ tip }, target) =>
        hideTooltip(target, tip)
      );
    });
  });

  const MutationObserverCtor = win?.MutationObserver;
  if (MutationObserverCtor && doc.body) {
    const observer = new MutationObserverCtor(() => {
      ACTIVE_TOOLTIP_TARGETS.forEach(({ tip }, target) => {
        if (!target.isConnected || !tip.isConnected) {
          hideTooltip(target, tip);
        }
      });
    });
    observer.observe(doc.body, { childList: true, subtree: true });
  }
}

/**
 * Attach delegated hover handlers for filter chip tooltip containers.
 *
 * @param {Document} doc - Document hosting the filter chip tooltips.
 * @returns {void}
 */
function attachFilterTooltipDelegation(doc) {
  if (!doc || FILTER_TOOLTIP_DELEGATION_DOCS.has(doc)) return;
  const containers = doc.querySelectorAll(FILTER_TOOLTIP_CONTAINERS);
  if (!containers.length) return;
  FILTER_TOOLTIP_DELEGATION_DOCS.add(doc);

  const isFilterTooltipButton = (btn) =>
    Boolean(btn?.closest?.(FILTER_TOOLTIP_CONTAINERS));

  doc.addEventListener(
    'pointerover',
    (event) => {
      const btn = event.target.closest(
        'button[data-focus-tooltip-id][data-focus-tooltip]'
      );
      if (!btn || !isFilterTooltipButton(btn)) return;
      const tip = FILTER_TIP_BY_BUTTON.get(btn);
      if (!tip) return;
      activateTooltip(btn, tip);
    },
    { capture: true }
  );

  doc.addEventListener(
    'pointerout',
    (event) => {
      const btn = event.target.closest('button[data-focus-tooltip-id]');
      if (!btn || !isFilterTooltipButton(btn)) return;
      if (event.relatedTarget && btn.contains(event.relatedTarget)) return;
      const tip = FILTER_TIP_BY_BUTTON.get(btn);
      if (!tip) return;
      hideTooltip(btn, tip);
    },
    { capture: true }
  );
}

/**
 * Decide whether a tooltip should flip to remain within viewport insets.
 *
 * @param {object} options
 * @param {string} options.placement - Current tooltip placement.
 * @param {number} options.rectTop - Tooltip top edge (viewport coordinates).
 * @param {number} options.rectBottom - Tooltip bottom edge (viewport coordinates).
 * @param {number} options.viewportHeight - Viewport height.
 * @param {number} options.topInset - Minimum top inset for visibility.
 * @param {number} options.bottomInset - Minimum bottom inset for visibility.
 * @returns {string} Placement to use after considering available space.
 */
export function chooseTooltipPlacement({
  placement,
  rectTop,
  rectBottom,
  viewportHeight,
  topInset,
  bottomInset,
}) {
  if (placement !== 'top' && placement !== 'bottom') return placement;
  if (placement === 'bottom' && rectBottom > viewportHeight - bottomInset) {
    return 'top';
  }
  if (placement === 'top' && rectTop < topInset) {
    return 'bottom';
  }
  return placement;
}

/**
 * Adjust tooltip bubble horizontal position to stay within the viewport.
 *
 * @param {HTMLElement} target - Element owning the tooltip.
 * @param {HTMLElement} tip - Tooltip bubble element.
 * @param {object} [options]
 * @param {number} [options.padding=16] - Minimum viewport padding in pixels.
 * @returns {void}
 */
export function positionTooltipBubble(target, tip, options = {}) {
  if (!target || !tip || typeof window === 'undefined') return;
  const { padding = 16, offset = TOOLTIP_OFFSET_PX } = options;
  const viewportWidth =
    window.innerWidth || document.documentElement?.clientWidth || 0;
  const viewportHeight =
    window.innerHeight || document.documentElement?.clientHeight || 0;
  if (!viewportWidth || !viewportHeight) return;

  const nav = document.querySelector('.top-nav');
  const navHeight = nav?.getBoundingClientRect?.().height || 0;
  const topInset = Math.max(padding, navHeight + padding);
  const bottomInset = padding;

  const placement = tip.getAttribute('data-placement') || 'top';
  const targetRect = target.getBoundingClientRect();
  const tipRect = tip.getBoundingClientRect();
  const computeCoords = (placementValue) => {
    switch (placementValue) {
      case 'bottom':
        return {
          top: targetRect.bottom + offset,
          left: targetRect.left + targetRect.width / 2 - tipRect.width / 2,
        };
      case 'left':
        return {
          top: targetRect.top + targetRect.height / 2 - tipRect.height / 2,
          left: targetRect.left - tipRect.width - offset,
        };
      case 'right':
        return {
          top: targetRect.top + targetRect.height / 2 - tipRect.height / 2,
          left: targetRect.right + offset,
        };
      case 'top':
      default:
        return {
          top: targetRect.top - tipRect.height - offset,
          left: targetRect.left + targetRect.width / 2 - tipRect.width / 2,
        };
    }
  };

  let coords = computeCoords(placement);
  let nextPlacement = chooseTooltipPlacement({
    placement,
    rectTop: coords.top,
    rectBottom: coords.top + tipRect.height,
    viewportHeight,
    topInset,
    bottomInset,
  });

  if (nextPlacement !== placement) {
    tip.classList.remove(`focus-tooltip--${placement}`);
    tip.classList.add(`focus-tooltip--${nextPlacement}`);
    tip.setAttribute('data-placement', nextPlacement);
    coords = computeCoords(nextPlacement);
  }

  const shift = computeTooltipShift(
    coords.left,
    coords.left + tipRect.width,
    viewportWidth,
    padding
  );
  tip.style.left = `${coords.left + shift}px`;
  tip.style.top = `${coords.top}px`;
}

/**
 * Compute a horizontal shift to clamp a tooltip within the viewport.
 *
 * @param {number} left - Tooltip left coordinate.
 * @param {number} right - Tooltip right coordinate.
 * @param {number} viewportWidth - Visible viewport width.
 * @param {number} padding - Minimum padding from viewport edges.
 * @returns {number} Pixel shift to apply.
 */
export function computeTooltipShift(left, right, viewportWidth, padding) {
  const minX = padding;
  const maxX = viewportWidth - padding;
  let shift = 0;
  if (left < minX) shift += minX - left;
  if (right > maxX) shift += maxX - right;
  return shift;
}

/**
 * Schedule tooltip bubble positioning on hover/focus.
 *
 * @param {HTMLElement} target - Tooltip target element.
 * @param {HTMLElement} tip - Tooltip bubble element.
 * @returns {void}
 */
function attachTooltipPositioning(target, tip) {
  if (!target || !tip) return;
  if (TOOLTIP_LISTENERS.has(target)) return;
  attachGlobalTooltipListeners(target.ownerDocument || document);
  if (!resizeListenerAttached && typeof window !== 'undefined') {
    window.addEventListener('resize', handleTooltipResize, { passive: true });
    resizeListenerAttached = true;
  }
  if (!scrollListenerAttached && typeof window !== 'undefined') {
    window.addEventListener('scroll', handleTooltipResize, {
      passive: true,
      capture: true,
    });
    scrollListenerAttached = true;
  }
  const handlePointerEnter = () => {
    activateTooltip(target, tip);
  };
  const handleFocusIn = () => {
    const supportsFocusVisible =
      typeof CSS !== 'undefined' && CSS.supports?.('selector(:focus-visible)');
    const focusVisible =
      supportsFocusVisible && Boolean(target.matches?.(':focus-visible'));
    if (!lastInputWasKeyboard && !focusVisible) return;
    activateTooltip(target, tip);
  };
  const deactivate = () => {
    deactivateTooltip(target, tip);
  };
  const controller = new AbortController();
  const { signal } = controller;
  TOOLTIP_LISTENERS.set(target, controller);
  target.addEventListener('pointerenter', handlePointerEnter, { signal });
  target.addEventListener('pointerleave', deactivate, { signal });
  target.addEventListener('pointercancel', deactivate, { signal });
  target.addEventListener('focus', handleFocusIn, { signal });
  target.addEventListener('focusin', handleFocusIn, { signal });
  target.addEventListener('blur', deactivate, { signal });
  target.addEventListener('focusout', deactivate, { signal });
}

/**
 * Check whether the given element is part of the dialpad accordion.
 *
 * @param {HTMLElement|Element|null} el - Potential dialpad descendant.
 * @returns {boolean} True when the element lives inside #dialpad.
 */
function isDialpadElement(el) {
  return Boolean(el?.closest?.('#dialpad'));
}

/**
 * Determine if an element is an AI accordion summary toggle.
 *
 * @param {HTMLElement|Element|null} el - Node to check.
 * @returns {boolean} True when the element is a <summary> under .ai-accordion.
 */
function isAiAccordionSummary(el) {
  return Boolean(el?.closest?.('.ai-accordion')) && el?.tagName === 'SUMMARY';
}

/**
 * Resolve tooltip text, falling back to accordion labels when missing.
 *
 * @param {HTMLElement|Element|null} el - Element receiving the tooltip.
 * @param {string} [defaultText=''] - Default text from config.
 * @returns {string} Tooltip content, or an empty string when none.
 */
function resolveTooltipText(el, defaultText = '') {
  const custom = (el?.dataset?.focusTooltip || '').trim();
  if (custom) return custom;

  if (isAiAccordionSummary(el)) {
    const label = (el.textContent || '').trim();
    return label
      ? `Expand ${label} to view AI research and saved notes.`
      : 'Expand to view AI research and saved notes.';
  }

  const fallback = (defaultText || '').trim();
  if (fallback) return fallback;

  return '';
}

/**
 * Remove previously injected tooltip attributes and nodes from the given DOM region.
 *
 * @param {HTMLElement} region - DOM subtree to clean.
 * @returns {void}
 */
function cleanupTooltipRegion(region) {
  const doc = region.ownerDocument || document;
  ACTIVE_TOOLTIP_TARGETS.forEach(({ tip }, target) => {
    if (
      target === region ||
      region.contains(target) ||
      !target.isConnected ||
      !tip.isConnected
    ) {
      hideTooltip(target, tip);
    }
  });
  region.removeAttribute(TOOLTIP_SIGNATURE_ATTR);
  region
    .querySelectorAll('[data-focus-tooltip-bubble-target]')
    .forEach((el) => {
      const controller = TOOLTIP_LISTENERS.get(el);
      if (controller) {
        controller.abort();
        TOOLTIP_LISTENERS.delete(el);
      }
    });
  region.querySelectorAll(`[${TOOLTIP_ATTR}]`).forEach((el) => {
    const id = el.getAttribute(TOOLTIP_ATTR);
    if (id) {
      doc.getElementById(id)?.remove();
    }
    el.removeAttribute(TOOLTIP_ATTR);
    el.removeAttribute('aria-describedby');
    el.removeAttribute('title');
    el.removeAttribute('data-focus-tooltip-bubble-target');
  });
  region.querySelectorAll('.focus-tooltip').forEach((tip) => tip.remove());
  region.querySelectorAll('.focus-tooltip-wrapper').forEach((wrapper) => {
    if (
      wrapper.tagName === 'SUMMARY' ||
      wrapper.hasAttribute('data-focus-tooltip')
    ) {
      wrapper.classList.remove('focus-tooltip-wrapper');
      wrapper.classList.remove('focus-tooltip-wrapper--block');
      return;
    }
    const parent = wrapper.parentElement;
    if (!parent) return;
    while (wrapper.firstChild) {
      parent.insertBefore(wrapper.firstChild, wrapper);
    }
    wrapper.remove();
  });
}

/**
 * Build a stable signature for tooltip targets in a region.
 *
 * @param {HTMLElement} region - DOM subtree to inspect.
 * @returns {string} Serialized tooltip target signature.
 */
function buildTooltipRegionSignature(region) {
  const seen = new WeakSet();
  const parts = [];
  const describeTarget = (el, tooltipText, placement, source) => {
    if (!el || seen.has(el)) return;
    if (isDialpadElement(el)) return;
    seen.add(el);
    const identity =
      el.id ||
      el.getAttribute('data-action') ||
      el.getAttribute('data-cta') ||
      el.getAttribute('href') ||
      (el.textContent || '').trim().slice(0, 60);
    parts.push(
      [
        source,
        el.tagName,
        identity,
        tooltipText,
        placement,
        el.disabled ? 'disabled' : 'enabled',
      ].join('|')
    );
  };

  TOOLTIP_CONFIG.forEach(({ selector, text, placement = 'top' }, index) => {
    region.querySelectorAll(selector).forEach((el) => {
      const tooltipText = resolveTooltipText(el, text);
      if (!tooltipText) return;
      const placementPref =
        el.dataset.focusTooltipPlacement || placement || 'top';
      describeTarget(el, tooltipText, placementPref, `config:${index}`);
    });
  });

  region.querySelectorAll('[data-focus-tooltip]').forEach((el) => {
    const tooltipText = resolveTooltipText(el);
    if (!tooltipText) return;
    const placementPref = el.dataset.focusTooltipPlacement || 'top';
    describeTarget(el, tooltipText, placementPref, 'inline');
  });

  return parts.join('\n');
}

/**
 * Wire up accessible tooltips within the Focus Mode start modal.
 *
 * Adds aria metadata and keyboard focusability to the modal’s status list,
 * upcoming task list, and start button. Cleans up any previously injected
 * hints so repeated renders stay idempotent.
 *
 * @param {HTMLElement|null} modal - Focus Mode start modal element.
 * @param {object} [options]
 * @param {boolean} [options.enableTooltips=true] - Whether tooltips are enabled.
 * @param {string} [options.startTooltipText] - Backward-compatible start CTA tooltip text.
 * @param {string} [options.startTooltipTextInactive] - Tooltip text when the start CTA is disabled.
 * @param {string} [options.startTooltipTextActive] - Tooltip text when the start CTA is enabled.
 * @param {string[]} [options.phaseTooltips=[]] - Tooltip strings for each status phase row.
 * @param {string[]} [options.taskTooltips=[]] - Tooltip strings for each task row.
 * @returns {void}
 */
export function applyFocusStartTooltips(modal, options = {}) {
  const {
    enableTooltips = true,
    startTooltipText,
    startTooltipTextInactive,
    startTooltipTextActive,
    phaseTooltips = [],
    taskTooltips = [],
  } = options;
  if (!modal) return;

  const doc = modal.ownerDocument || document;
  ensureTooltipRoot(doc);
  modal.setAttribute('data-focus-tooltips', enableTooltips ? 'true' : 'false');

  cleanupTooltipRegion(modal);
  if (!enableTooltips) return;

  const addTooltip = (target, text, placement = 'top') => {
    const tooltipText = (text || '').trim();
    if (!target || !tooltipText) return;
    const tooltipId = `focus-tooltip-start-${TOOLTIP_ID_SEQ++}`;
    const tip = doc.createElement('span');
    tip.id = tooltipId;
    tip.className = `focus-tooltip focus-tooltip--bubble focus-tooltip--${placement}`;
    tip.setAttribute('role', 'tooltip');
    tip.setAttribute('data-placement', placement);
    tip.textContent = tooltipText;
    const display =
      typeof window !== 'undefined' && window.getComputedStyle
        ? window.getComputedStyle(target).display
        : '';
    const isBlock = BLOCK_TOOLTIP_DISPLAYS.has(display);
    let wrapper = target.parentElement;
    if (isBlock) {
      target.classList.add(
        'focus-tooltip-wrapper',
        'focus-tooltip-wrapper--block'
      );
      wrapper = target;
    } else if (!wrapper?.classList.contains('focus-tooltip-wrapper')) {
      wrapper = doc.createElement('span');
      wrapper.className = 'focus-tooltip-wrapper';
      target.parentElement?.insertBefore(wrapper, target);
      wrapper.appendChild(target);
    }
    wrapper?.appendChild(tip);
    attachTooltipPositioning(target, tip);
    target.setAttribute(TOOLTIP_ATTR, tooltipId);
    target.setAttribute('aria-describedby', tooltipId);
    target.setAttribute('data-focus-tooltip-bubble-target', 'true');
    target.setAttribute(TOOLTIP_TEXT_ATTR, tooltipText);
  };

  const startBtn = modal.querySelector('#focusStartBtn');
  const isStartDisabled =
    startBtn?.disabled || startBtn?.getAttribute('disabled') !== null;
  const inactiveText =
    typeof startTooltipTextInactive === 'string' &&
    startTooltipTextInactive.trim()
      ? startTooltipTextInactive.trim()
      : '';
  const activeText =
    typeof startTooltipTextActive === 'string' && startTooltipTextActive.trim()
      ? startTooltipTextActive.trim()
      : '';
  const legacyText =
    typeof startTooltipText === 'string' && startTooltipText.trim()
      ? startTooltipText.trim()
      : '';
  const startText =
    (isStartDisabled ? inactiveText : activeText) ||
    legacyText ||
    "Get started on today's work.";
  addTooltip(startBtn, startText, 'bottom');

  const retryBtn = modal.querySelector('#focusStartRetry');
  addTooltip(
    retryBtn,
    retryBtn?.dataset?.focusTooltip || 'Retry loading Focus Mode setup.',
    'bottom'
  );

  modal.querySelectorAll('#focusStartStatus li').forEach((item, idx) => {
    const text = phaseTooltips[idx];
    item.setAttribute('tabindex', '0');
    if (text) addTooltip(item, text, 'right');
  });

  modal.querySelectorAll('#focusStartList li').forEach((item, idx) => {
    const text = taskTooltips[idx];
    item.setAttribute('tabindex', '0');
    if (text) addTooltip(item, text, 'right');
  });
}

/**
 * Attach or remove tooltip markup for Focus Mode UI affordances.
 *
 * @param {HTMLElement|null} root - Root element containing the active card.
 * @param {object} [options]
 * @param {boolean} [options.enableTooltips=true] - Whether tooltips are enabled.
 * @param {HTMLElement[]} [options.extraRegions=[]] - Additional containers to scan.
 * @returns {void}
 */
export function applyFocusModeTooltips(root, options = {}) {
  const { enableTooltips = true, extraRegions = [] } = options;
  if (!root) return;

  ensureTooltipRoot(root.ownerDocument || document);
  const regions = [root, ...extraRegions.filter(Boolean)];
  const flag = enableTooltips ? 'true' : 'false';
  root.setAttribute('data-focus-tooltips', flag);
  if (typeof document !== 'undefined' && document.body) {
    document.body.setAttribute('data-focus-tooltips', flag);
  }

  if (!enableTooltips) {
    regions.forEach((region) => cleanupTooltipRegion(region));
    return;
  }
  attachFilterTooltipDelegation(root.ownerDocument || document);
  const regionsToBuild = [];

  regions.forEach((region) => {
    const signature = buildTooltipRegionSignature(region);
    const hasAppliedTooltips =
      !signature || !!region.querySelector(`[${TOOLTIP_ATTR}]`);
    if (
      region.getAttribute(TOOLTIP_SIGNATURE_ATTR) === signature &&
      hasAppliedTooltips
    ) {
      return;
    }
    cleanupTooltipRegion(region);
    region.setAttribute(TOOLTIP_SIGNATURE_ATTR, signature);
    regionsToBuild.push(region);
  });

  /**
   * Determine whether the element should use a bubble tooltip with a wrapper.
   *
   * @param {HTMLElement} el - Potential tooltip target.
   * @returns {boolean} Whether to render a custom bubble tooltip.
   */
  const shouldUseBubble = () => true;
  /**
   * Ensure the target element is wrapped so tooltip bubbles do not modify its text content.
   *
   * @param {HTMLElement} el - The element receiving the tooltip.
   * @returns {HTMLElement|null} Wrapper element for the tooltip and target.
   */
  const ensureTooltipWrapper = (el) => {
    if (!el) return null;
    const display =
      typeof window !== 'undefined' && window.getComputedStyle
        ? window.getComputedStyle(el).display
        : '';
    const isBlock = BLOCK_TOOLTIP_DISPLAYS.has(display);
    if (el.tagName === 'SUMMARY' || isBlock) {
      el.classList.add('focus-tooltip-wrapper');
      if (isBlock) {
        el.classList.add('focus-tooltip-wrapper--block');
      }
      return el;
    }
    const parent = el.parentElement;
    if (parent?.classList.contains('focus-tooltip-wrapper')) return parent;
    if (!parent) return null;
    const wrapper = document.createElement('span');
    wrapper.className = 'focus-tooltip-wrapper';
    parent.insertBefore(wrapper, el);
    wrapper.appendChild(el);
    return wrapper;
  };

  regionsToBuild.forEach((region) => {
    TOOLTIP_CONFIG.forEach(({ selector, text, placement = 'top' }) => {
      region.querySelectorAll(selector).forEach((el) => {
        if (isDialpadElement(el)) return;
        const tooltipText = resolveTooltipText(el, text);
        if (!tooltipText) return;
        el.setAttribute(TOOLTIP_TEXT_ATTR, tooltipText);
        const placementPref =
          el.dataset.focusTooltipPlacement || placement || 'top';
        const tooltipId = `focus-tooltip-${TOOLTIP_ID_SEQ++}`;
        const tip = document.createElement('span');
        const bubble = shouldUseBubble(el);
        tip.id = tooltipId;
        tip.className = bubble
          ? `focus-tooltip focus-tooltip--bubble focus-tooltip--${placementPref}`
          : 'sr-only focus-tooltip';
        if (bubble) {
          const wrapper = ensureTooltipWrapper(el);
          if (!wrapper) return;
          tip.setAttribute('data-focus-tooltip-bubble', '');
          tip.setAttribute('data-placement', placementPref);
          el.setAttribute('data-focus-tooltip-bubble-target', 'true');
          wrapper.appendChild(tip);
          attachTooltipPositioning(el, tip);
        } else {
          region.appendChild(tip);
        }
        tip.setAttribute('role', 'tooltip');
        tip.textContent = tooltipText;
        el.setAttribute(TOOLTIP_ATTR, tooltipId);
        el.setAttribute('aria-describedby', tooltipId);
        if (el.closest(FILTER_TOOLTIP_CONTAINERS)) {
          FILTER_TIP_BY_BUTTON.set(el, tip);
        }
      });
    });

    region.querySelectorAll('[data-focus-tooltip]').forEach((el) => {
      if (isDialpadElement(el)) return;
      if (el.getAttribute(TOOLTIP_ATTR)) return;
      const tooltipText = resolveTooltipText(el);
      if (!tooltipText) return;
      el.setAttribute(TOOLTIP_TEXT_ATTR, tooltipText);
      const placementPref = el.dataset.focusTooltipPlacement || 'top';
      const tooltipId = `focus-tooltip-${TOOLTIP_ID_SEQ++}`;
      const tip = document.createElement('span');
      const bubble = shouldUseBubble(el);
      tip.id = tooltipId;
      tip.className = bubble
        ? `focus-tooltip focus-tooltip--bubble focus-tooltip--${placementPref}`
        : 'sr-only focus-tooltip';
      if (bubble) {
        const wrapper = ensureTooltipWrapper(el);
        if (!wrapper) return;
        tip.setAttribute('data-focus-tooltip-bubble', '');
        tip.setAttribute('data-placement', placementPref);
        el.setAttribute('data-focus-tooltip-bubble-target', 'true');
        wrapper.appendChild(tip);
        attachTooltipPositioning(el, tip);
      } else {
        region.appendChild(tip);
      }
      tip.setAttribute('role', 'tooltip');
      tip.textContent = tooltipText;
      el.setAttribute(TOOLTIP_ATTR, tooltipId);
      el.setAttribute('aria-describedby', tooltipId);
      if (el.closest(FILTER_TOOLTIP_CONTAINERS)) {
        FILTER_TIP_BY_BUTTON.set(el, tip);
      }
    });
  });
}
